/**
 * FeedbackInboxCard — super-admin surface for the free-text user feedback
 * submitted from every dashboard page via <FeedbackWidget />.
 *
 * Shows a live list of feedback with filters (open / resolved · feature / bug
 * / general), inline resolve/reopen toggle, admin-note field, and delete
 * button for spam.
 */
import { useEffect, useState, useMemo } from "react";
import { Bug, Lightbulb, MessageCircle, CheckCircle2, Undo2, Trash2, Filter, Reply as ReplyIcon, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const KIND_META = {
    feature: { label: "Feature",  icon: Lightbulb,     tone: "text-emerald-500 bg-emerald-500/10 border-emerald-500/40" },
    bug:     { label: "Bug",      icon: Bug,           tone: "text-red-500 bg-red-500/10 border-red-500/40" },
    general: { label: "General",  icon: MessageCircle, tone: "text-primary bg-primary/10 border-primary/40" },
};

export default function FeedbackInboxCard() {
    const [items, setItems]         = useState([]);
    const [openCount, setOpenCount] = useState(0);
    const [status, setStatus]       = useState("open");  // 'open' | 'resolved' | 'all'
    const [kind, setKind]           = useState("all");   // 'all' | 'feature' | 'bug' | 'general'
    const [loading, setLoading]     = useState(true);

    async function load() {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (status !== "all") params.set("status", status);
            if (kind   !== "all") params.set("kind", kind);
            const r = await api.get(`/admin/feedback${params.toString() ? `?${params}` : ""}`);
            setItems(r.data.items || []);
            setOpenCount(r.data.open_count || 0);
        } catch (e) {
            toast.error("Couldn't load feedback");
            setItems([]);
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, kind]);

    const counts = useMemo(() => {
        const c = { feature: 0, bug: 0, general: 0 };
        for (const it of items) c[it.kind] = (c[it.kind] || 0) + 1;
        return c;
    }, [items]);

    return (
        <div className="space-y-4" data-testid="admin-feedback-inbox">
            <header className="flex flex-wrap items-center gap-3 justify-between">
                <div>
                    <div className="overline">User feedback inbox</div>
                    <div className="font-black text-2xl">
                        {openCount} open <span className="text-muted-foreground text-base font-semibold">· {items.length} shown</span>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2 items-center text-xs">
                    <Filter className="w-3.5 h-3.5 text-muted-foreground" />
                    <SegSelect value={status} setValue={setStatus} testIdPrefix="feedback-status"
                        opts={[["open","Open"],["resolved","Resolved"],["all","All"]]} />
                    <SegSelect value={kind} setValue={setKind} testIdPrefix="feedback-kind"
                        opts={[["all","All"],["feature",`Feature (${counts.feature})`],
                               ["bug",`Bug (${counts.bug})`],["general",`General (${counts.general})`]]} />
                </div>
            </header>

            {loading ? (
                <div className="text-sm text-muted-foreground">Loading feedback…</div>
            ) : items.length === 0 ? (
                <div className="border border-dashed border-border rounded p-6 text-center text-sm text-muted-foreground"
                     data-testid="feedback-empty">
                    Nothing here. When users tap the feedback pill on any dashboard,
                    their notes will land here.
                </div>
            ) : (
                <ul className="space-y-3">
                    {items.map((it) => (
                        <FeedbackRow key={it.id} item={it} onChanged={load} />
                    ))}
                </ul>
            )}
        </div>
    );
}

function SegSelect({ value, setValue, opts, testIdPrefix }) {
    return (
        <div className="inline-flex rounded border border-border overflow-hidden">
            {opts.map(([v, label]) => (
                <button
                    key={v}
                    onClick={() => setValue(v)}
                    data-testid={`${testIdPrefix}-${v}`}
                    className={`px-2.5 py-1 font-semibold ${
                        value === v
                            ? "bg-primary text-primary-foreground"
                            : "bg-transparent text-foreground hover:bg-muted"
                    }`}
                >
                    {label}
                </button>
            ))}
        </div>
    );
}

function FeedbackRow({ item, onChanged }) {
    const [note, setNote]           = useState(item.admin_note || "");
    const [replyText, setReplyText] = useState("");
    const [replyOpen, setReplyOpen] = useState(false);
    const [busy, setBusy]           = useState(false);
    const meta = KIND_META[item.kind] || KIND_META.general;
    const Icon = meta.icon;

    const resolve = async (resolved) => {
        setBusy(true);
        try {
            await api.patch(`/admin/feedback/${item.id}`, { resolved, admin_note: note });
            toast.success(resolved ? "Marked resolved" : "Reopened");
            onChanged();
        } catch { toast.error("Update failed"); }
        finally { setBusy(false); }
    };

    const sendReply = async () => {
        const msg = replyText.trim();
        if (msg.length < 3) {
            toast.error("Reply is too short (3+ chars)");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/admin/feedback/${item.id}/reply`, { message: msg });
            toast.success("Reply sent — user gets an in-app notification");
            setReplyText("");
            setReplyOpen(false);
            onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reply failed");
        } finally { setBusy(false); }
    };

    const remove = async () => {
        if (!confirm("Delete this feedback? Cannot be undone.")) return;
        setBusy(true);
        try {
            await api.delete(`/admin/feedback/${item.id}`);
            toast.success("Deleted");
            onChanged();
        } catch { toast.error("Delete failed"); }
        finally { setBusy(false); }
    };

    const replies = item.admin_replies || [];

    return (
        <li className={`rounded-lg border ${item.resolved ? "border-border bg-card/50 opacity-70" : "border-border bg-card"} p-4 space-y-3`}
            data-testid={`feedback-row-${item.id}`}>
            <div className="flex items-start gap-3 flex-wrap">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-extrabold uppercase tracking-widest ${meta.tone}`}>
                    <Icon className="w-3 h-3" /> {meta.label}
                </span>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold truncate">
                        {item.user_name || "—"}
                        <span className="text-muted-foreground font-normal"> · {item.user_email || "no-email"}</span>
                        <span className="text-muted-foreground font-mono text-[10px] ml-2 uppercase">{item.role || "trader"}</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground font-mono truncate">
                        {new Date(item.created_at).toLocaleString()} · page: {item.page_path || "—"}
                        {replies.length > 0 && (
                            <span className="ml-2 text-primary font-bold">
                                · {replies.length} repl{replies.length === 1 ? "y" : "ies"} sent
                            </span>
                        )}
                    </div>
                </div>
                {item.resolved && (
                    <span className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest">
                        ✓ Resolved{item.resolved_by ? ` · ${item.resolved_by}` : ""}
                    </span>
                )}
            </div>

            <div className="text-sm whitespace-pre-wrap leading-relaxed border-l-2 border-primary/60 pl-3">
                {item.message}
            </div>

            {/* Reply history */}
            {replies.length > 0 && (
                <div className="rounded border border-border bg-background/60 p-2 space-y-1.5"
                     data-testid={`feedback-replies-${item.id}`}>
                    <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                        Replies sent
                    </div>
                    {replies.map((r, i) => (
                        <div key={r.ts || `reply-${i}`} className="text-xs">
                            <span className="text-muted-foreground font-mono text-[10px]">
                                {new Date(r.ts).toLocaleString()} · {r.admin_email || "admin"}:
                            </span>
                            <span className="ml-1 whitespace-pre-wrap">{r.message}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Reply composer (collapsed → expanded) */}
            {replyOpen && (
                <div className="rounded border border-primary/40 bg-primary/5 p-2 space-y-2"
                     data-testid={`feedback-reply-composer-${item.id}`}>
                    <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        rows={2}
                        maxLength={2000}
                        autoFocus
                        placeholder="Type your reply — the user gets an in-app notification instantly."
                        className="w-full text-xs px-2 py-1.5 border border-border bg-background rounded resize-y"
                        data-testid={`feedback-reply-text-${item.id}`}
                    />
                    <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                        <span>{replyText.length}/2000 · in-app notification only (closed-loop)</span>
                        <div className="inline-flex gap-1">
                            <button
                                type="button"
                                onClick={() => { setReplyOpen(false); setReplyText(""); }}
                                className="px-2 py-1 rounded border border-border text-foreground hover:border-primary font-semibold"
                                data-testid={`feedback-reply-cancel-${item.id}`}
                            >Cancel</button>
                            <button
                                type="button"
                                onClick={sendReply}
                                disabled={busy || replyText.trim().length < 3}
                                data-testid={`feedback-reply-send-${item.id}`}
                                className="px-3 py-1 rounded bg-primary text-primary-foreground font-bold inline-flex items-center gap-1 disabled:opacity-60"
                            ><Send className="w-3 h-3" /> Send reply</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="grid gap-2 md:grid-cols-[1fr,auto,auto,auto,auto] items-end">
                <label className="block">
                    <span className="block text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                        Internal note (optional)
                    </span>
                    <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={2}
                        maxLength={2000}
                        placeholder="Ship in v2 / duplicate of #… / needs repro / etc."
                        className="w-full text-xs px-2 py-1.5 border border-border bg-background rounded resize-y"
                        data-testid={`feedback-note-${item.id}`}
                    />
                </label>
                {!replyOpen && (
                    <button
                        type="button" onClick={() => setReplyOpen(true)} disabled={busy}
                        data-testid={`feedback-reply-${item.id}`}
                        className="text-xs font-bold px-3 py-2 rounded border border-primary/50 text-primary hover:bg-primary/10 disabled:opacity-60 inline-flex items-center gap-1"
                    ><ReplyIcon className="w-3.5 h-3.5" /> Reply</button>
                )}
                {!item.resolved ? (
                    <button
                        type="button" onClick={() => resolve(true)} disabled={busy}
                        data-testid={`feedback-resolve-${item.id}`}
                        className="text-xs font-bold px-3 py-2 rounded bg-primary text-primary-foreground disabled:opacity-60 inline-flex items-center gap-1"
                    ><CheckCircle2 className="w-3.5 h-3.5" /> Resolve</button>
                ) : (
                    <button
                        type="button" onClick={() => resolve(false)} disabled={busy}
                        data-testid={`feedback-reopen-${item.id}`}
                        className="text-xs font-bold px-3 py-2 rounded border border-border text-foreground hover:border-primary disabled:opacity-60 inline-flex items-center gap-1"
                    ><Undo2 className="w-3.5 h-3.5" /> Reopen</button>
                )}
                <button
                    type="button" onClick={remove} disabled={busy}
                    data-testid={`feedback-delete-${item.id}`}
                    title="Delete"
                    className="text-xs font-bold px-2 py-2 rounded border border-red-500/40 text-red-500 hover:bg-red-500/10 disabled:opacity-60 inline-flex items-center"
                ><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
        </li>
    );
}
